<script lang="ts">
	import * as Dialog from '$lib/components/ui/dialog';
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import { Textarea } from '$lib/components/ui/textarea';
	import {
		CloudUpload,
		CheckCircle,
		Loader2,
		X,
		FileText,
		Building,
		AlertCircle,
		UserPlus,
		RefreshCw,
		ExternalLink
	} from '@lucide/svelte';
	import { cvUploadTest } from '$lib/components/tracking/ab-tests';
	import { useTest } from '$lib/components/tracking/useTest';
	import { PUBLIC_DASHBOARD_URL } from '$env/static/public';
	import { fetchSSE } from '$lib/api';
	import { STAGE_LABELS } from '$lib/config/consts';
	import { isDashboardAuthenticated } from '$lib/utils/auth-cookie';

	interface Props {
		open: boolean;
		initialFile?: File | null;
		onSuccess?: () => void;
		onError?: (msg: string) => void;
	}

	interface ParsedDocData {
		email?: string;
		company_email?: string;
		company_website?: string;
		company_name?: string;
		job_title?: string;
		description?: string;
		location?: string;
		[key: string]: unknown;
	}

	interface DropSSEResult {
		success: boolean;
		type: 'cv' | 'jd' | 'unknown';
		saved: boolean;
		id?: string;
		token?: string;
		data?: ParsedDocData;
		missing_fields?: string[];
		confidence?: number;
		error?: string;
		updated?: boolean;
	}

	interface LanguageField {
		language: string;
		proficiency?: string;
	}

	interface ExperienceField {
		title: string;
		company: string;
	}

	interface EducationField {
		degree: string;
		institution: string;
	}

	interface LinkField {
		url: string;
		label?: string;
	}

	let { open = $bindable(), initialFile = null, onSuccess, onError }: Props = $props();

	const test = useTest(cvUploadTest);
	const APP_URL = PUBLIC_DASHBOARD_URL || 'https://app.talentprofile.net';

	type Step = 'idle' | 'uploading' | 'missing-fields' | 'success' | 'error' | 'redirect-dashboard';
	let step = $state<Step>('idle');
	let progress_stage = $state('');
	let progress_completed = $state(0);
	let progress_total = $state(0);
	let message = $state('');
	let fileInputRef = $state<HTMLInputElement | null>(null);
	let wasUpdated = $state(false);
	let isAppUser = $state(false);

	let docType = $state<'cv' | 'jd' | 'unknown'>('cv');
	let parsedData = $state<ParsedDocData | null>(null);
	let missingFields = $state<string[]>([]);
	let partialFields = $state<Record<string, unknown>>({});

	let email = $state('');
	let companyWebsite = $state('');
	let companyEmail = $state('');
	let companyName = $state('');
	let jobTitle = $state('');
	let jobDescription = $state('');
	let jobLocation = $state('');
	let formError = $state('');

	const FIELD_LABELS: Record<string, string> = {
		profile_name: 'Name',
		email: 'Email',
		job_title: 'Job Title',
		location: 'Location',
		description: 'Summary',
		total_experience: 'Experience',
		skills: 'Skills',
		languages: 'Languages',
		experience: 'Work Experience',
		education: 'Education',
		certifications: 'Certifications',
		links: 'Links',
		company_name: 'Company',
		company_email: 'Company Email',
		company_website: 'Website',
		skills_required: 'Required Skills',
		requirements: 'Requirements',
		responsibilities: 'Responsibilities',
		salary_range: 'Salary',
		employment_type: 'Employment Type',
		remote_type: 'Remote Policy'
	};

	const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	$effect(() => {
		if (!open) {
			step = 'idle';
			message = '';
			progress_stage = '';
			progress_completed = 0;
			progress_total = 0;
			parsedData = null;
			missingFields = [];
			partialFields = {};
			email = '';
			companyWebsite = '';
			companyEmail = '';
			companyName = '';
			jobTitle = '';
			jobDescription = '';
			jobLocation = '';
			formError = '';
			wasUpdated = false;
		} else {
			isAppUser = isDashboardAuthenticated();
		}
	});

	$effect(() => {
		if (open && initialFile && step === 'idle') {
			processFile(initialFile);
		}
	});

	function formatFieldValue(key: string, value: unknown): string {
		if (value === null || value === undefined) return '';
		if (typeof value === 'string') return value;
		if (Array.isArray(value)) {
			if (value.length === 0) return '';
			const first = value[0];
			if (typeof first === 'string') return value.join(', ');
			if (typeof first === 'object' && first !== null) {
				if ('language' in first)
					return (value as LanguageField[]).map((l) => l.language).join(', ');
				if ('title' in first)
					return (value as ExperienceField[]).map((e) => `${e.title} at ${e.company}`).join(' • ');
				if ('degree' in first)
					return (value as EducationField[])
						.map((e) => `${e.degree} — ${e.institution}`)
						.join(' • ');
				if ('url' in first) return (value as LinkField[]).map((l) => l.url).join(', ');
			}
			return JSON.stringify(value);
		}
		return String(value);
	}

	const hasPartialData = $derived(Object.keys(partialFields).length > 0);

	const partialEntries = $derived(
		Object.entries(partialFields).filter(([, value]) => formatFieldValue('', value) !== '')
	);

	const pendingKeys = $derived(
		Object.keys(FIELD_LABELS)
			.filter((k) => !(k in partialFields))
			.slice(0, 3)
	);

	async function processFile(file: File) {
		if (file.size > 10 * 1024 * 1024) {
			step = 'error';
			message = 'File too large (max 10MB)';
			return;
		}

		test.event('file-selected', file.size);
		step = 'uploading';
		progress_stage = 'uploading';
		progress_completed = 0;
		progress_total = 4;
		partialFields = {};
		test.event('upload-started');

		try {
			const form_data = new FormData();
			form_data.append('file', file);
			form_data.append('ab_test', test.id);
			form_data.append('ab_variant', test.variant);

			const result = await fetchSSE<DropSSEResult>(
				{
					url: `/drop`,
					method: 'POST',
					formData: form_data
				},
				{
					onProgress: (event) => {
						progress_stage = event.stage;
						if (event.completed !== undefined) progress_completed = event.completed;
						if (event.total !== undefined) progress_total = event.total;
					},
					onField: (key, value) => {
						partialFields = { ...partialFields, [key]: value };
					},
					onFieldReset: () => {
						partialFields = {};
					}
				}
			);

			if (!result.success) {
				throw new Error(result.error || 'Failed to process file');
			}

			test.event('upload-completed');
			test.event('parse-completed');

			docType = result.type;
			wasUpdated = result.updated || false;

			if (docType === 'unknown') {
				step = 'error';
				message =
					"We couldn't identify this document as a CV or Job Description. Please make sure it contains clear professional information.";
				test.event('upload-failed');
				onError?.(message);
				return;
			}

			if (result.saved && result.id) {
				if (result.token) {
					localStorage.setItem(
						'dropProfileData',
						JSON.stringify({
							type: docType,
							id: result.id,
							token: result.token
						})
					);
					window.dispatchEvent(new Event('auth-changed'));
				}

				step = 'success';
				if (docType === 'jd') {
					message = "Job posted! We'll match you with candidates.";
				} else if (wasUpdated) {
					message = "Profile updated with your latest CV! We'll keep sending you matches.";
				} else {
					message = "Profile saved! We'll send job matches to your email.";
				}
				test.event('record-saved');
				test.goal(1);
				onSuccess?.();
			} else {
				if (isAppUser) {
					step = 'redirect-dashboard';
					return;
				}

				parsedData = (result.data as ParsedDocData) || null;
				missingFields = result.missing_fields || [];
				step = 'missing-fields';
				test.event('form-viewed');
				if (parsedData?.email) email = parsedData.email;
				if (parsedData?.company_email) companyEmail = parsedData.company_email;
				if (parsedData?.company_website) companyWebsite = parsedData.company_website;
				if (parsedData?.company_name) companyName = parsedData.company_name as string;
				if (parsedData?.job_title) jobTitle = parsedData.job_title as string;
				if (parsedData?.description) jobDescription = parsedData.description as string;
				if (parsedData?.location) jobLocation = parsedData.location as string;
			}
		} catch (e) {
			step = 'error';
			message = e instanceof Error ? e.message : 'Something went wrong';
			test.event('upload-failed');
			onError?.(message);
		}
	}

	async function submitData() {
		test.event('form-submitted');
		step = 'uploading';
		progress_stage = 'saving';
		progress_completed = 0;
		progress_total = 0;
		partialFields = {};

		try {
			const submitPayload: Record<string, unknown> = {
				...parsedData,
				email: email || parsedData?.email,
				company_email: companyEmail || parsedData?.company_email,
				company_website: companyWebsite || parsedData?.company_website
			};

			if (docType === 'jd') {
				if (companyName) submitPayload.company_name = companyName;
				if (jobTitle) submitPayload.job_title = jobTitle;
				if (jobDescription) submitPayload.description = jobDescription;
				if (jobLocation) submitPayload.location = jobLocation;
			}

			const result = await fetchSSE<{
				success: boolean;
				id?: string;
				token?: string;
				error?: string;
				updated?: boolean;
			}>(
				{
					url: `/drop/complete`,
					method: 'POST',
					json: {
						type: docType,
						data: submitPayload,
						ab_test: test.id,
						ab_variant: test.variant
					}
				},
				{
					onProgress: (event) => {
						progress_stage = event.stage;
					}
				}
			);

			if (!result.success) {
				throw new Error(result.error || 'Failed to save');
			}

			test.event('record-saved');
			test.goal(1);
			wasUpdated = result.updated || false;

			if (result.id && result.token) {
				localStorage.setItem(
					'dropProfileData',
					JSON.stringify({
						type: docType,
						id: result.id,
						token: result.token
					})
				);
				window.dispatchEvent(new Event('auth-changed'));
			}

			step = 'success';
			if (docType === 'jd') {
				message = "Job posted! We'll match you with candidates.";
			} else if (wasUpdated) {
				message = "Profile updated with your latest CV! We'll keep sending you matches.";
			} else {
				message = "Profile saved! We'll send job matches to your email.";
			}
			onSuccess?.();
		} catch (e) {
			step = 'error';
			message = e instanceof Error ? e.message : 'Failed to save';
			onError?.(message);
		}
	}

	function handleFileSelect(e: Event) {
		const file = (e.target as HTMLInputElement).files?.[0];
		if (file) processFile(file);
	}

	function handleMissingFieldsSubmit() {
		formError = '';

		if (docType === 'cv' && missingFields.includes('email')) {
			if (!email || !EMAIL_REGEX.test(email)) {
				formError = 'Please enter a valid email';
				return;
			}
		}

		if (docType === 'jd') {
			if (missingFields.includes('company_contact')) {
				if (!companyEmail && !companyWebsite) {
					formError = 'Please provide company email or website';
					return;
				}
				if (companyEmail && !EMAIL_REGEX.test(companyEmail)) {
					formError = 'Please enter a valid company email';
					return;
				}
			}
			if (missingFields.includes('job_title') && !jobTitle) {
				formError = 'Please enter a job title';
				return;
			}
		}

		submitData();
	}

	function reset() {
		step = 'idle';
		message = '';
		progress_stage = '';
		wasUpdated = false;
		partialFields = {};
		if (fileInputRef) fileInputRef.value = '';
	}

	function handleGoToApp() {
		const target = docType === 'jd' ? '/company/dashboard' : '/talent/profile';
		window.open(`${APP_URL}${target}`, '_blank');
		open = false;
	}

	function handleSignUp() {
		window.open(`${APP_URL}/sign-up`, '_blank');
		open = false;
	}
</script>

<Dialog.Root bind:open>
	<Dialog.Content class="w-full max-w-2xl p-8">
		<Dialog.Header>
			<Dialog.Title>
				{#if step === 'success'}
					{#if docType === 'jd'}
						Job Posted!
					{:else if wasUpdated}
						Profile Updated!
					{:else}
						Profile Saved!
					{/if}
				{:else if step === 'redirect-dashboard'}
					You're already signed in
				{:else if step === 'missing-fields'}
					Almost there
				{:else if step === 'error'}
					Oops
				{:else if step === 'uploading' && hasPartialData}
					Analyzing Your Document...
				{:else}
					Upload CV or Job Description
				{/if}
			</Dialog.Title>
		</Dialog.Header>

		<div class="py-4">
			{#if step === 'idle'}
				<input
					bind:this={fileInputRef}
					type="file"
					accept=".pdf,.doc,.docx,.txt"
					class="hidden"
					onchange={handleFileSelect}
				/>

				<button
					type="button"
					onclick={() => fileInputRef?.click()}
					class="w-full rounded-lg border-2 border-dashed border-gray-300 p-8 text-center transition-colors hover:border-blue-400 hover:bg-blue-50"
				>
					<CloudUpload class="mx-auto mb-3 h-10 w-10 text-gray-400" />
					<p class="font-medium text-gray-700">Drop your file here</p>
					<p class="mt-1 text-sm text-gray-500">PDF, DOC, DOCX, or TXT • max 10MB</p>
				</button>

				<div class="mt-4 grid grid-cols-2 gap-3 text-sm">
					<div class="flex items-center gap-2 text-gray-600">
						<FileText class="h-4 w-4 text-blue-500" />
						<span>CV/Resume</span>
					</div>
					<div class="flex items-center gap-2 text-gray-600">
						<Building class="h-4 w-4 text-green-500" />
						<span>Job Description</span>
					</div>
				</div>

				<p class="mt-4 text-center text-xs text-gray-500">We auto-detect if it's a CV or JD</p>
			{:else if step === 'uploading'}
				<div class="flex flex-col items-center py-4">
					{#if !hasPartialData}
						<Loader2 class="mb-4 h-10 w-10 animate-spin text-blue-600" />
					{/if}
					<p class="font-medium text-gray-900">
						{STAGE_LABELS[progress_stage] || 'Processing...'}
					</p>
					{#if progress_total > 0 && !hasPartialData}
						<div class="mt-4 w-full max-w-xs">
							<div class="h-2 w-full overflow-hidden rounded-full bg-gray-200">
								<div
									class="h-full rounded-full bg-blue-600 transition-all duration-500"
									style="width: {Math.min(
										100,
										Math.max(0, Math.round((progress_completed / progress_total) * 100))
									)}%"
								></div>
							</div>
							<p class="mt-1 text-center text-xs text-gray-500">
								Step {progress_completed} of {progress_total}
							</p>
						</div>
					{/if}

					{#if hasPartialData}
						<div class="mt-4 w-full space-y-2">
							{#each partialEntries as [key, value] (key)}
								<div
									class="animate-in fade-in slide-in-from-left-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
								>
									<p class="text-xs font-medium text-gray-500">{FIELD_LABELS[key] || key}</p>
									<p class="line-clamp-2 text-sm text-gray-900">{formatFieldValue(key, value)}</p>
								</div>
							{/each}

							{#each pendingKeys as pendingKey (pendingKey)}
								<div class="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
									<div class="h-3 w-16 animate-pulse rounded bg-gray-200"></div>
									<div class="mt-1 h-4 w-3/4 animate-pulse rounded bg-gray-200"></div>
								</div>
							{/each}
						</div>
					{/if}
				</div>
			{:else if step === 'redirect-dashboard'}
				<div class="flex flex-col items-center py-6 text-center">
					<div class="mb-4 rounded-full bg-blue-100 p-3">
						<ExternalLink class="h-8 w-8 text-blue-600" />
					</div>
					<p class="mb-2 font-medium text-gray-900">
						{docType === 'cv'
							? 'Upload your CV from the dashboard for the best experience.'
							: 'Create your job posting from the dashboard.'}
					</p>
					<p class="mb-4 text-sm text-gray-600">
						Your document was parsed successfully. Continue in the dashboard where it will be linked
						to your account automatically.
					</p>
					<Button class="w-full gap-2" onclick={handleGoToApp}>
						<ExternalLink class="h-4 w-4" />
						Go to Dashboard
					</Button>
					<button
						type="button"
						class="mt-3 w-full text-sm text-gray-500 hover:text-gray-700"
						onclick={() => (open = false)}
					>
						Maybe later
					</button>
				</div>
			{:else if step === 'missing-fields'}
				<div class="space-y-4">
					<div class="flex items-center gap-2 rounded-lg bg-blue-50 p-3">
						{#if docType === 'jd'}
							<Building class="h-5 w-5 text-green-600" />
							<span class="text-sm font-medium text-green-800">Detected: Job Description</span>
						{:else}
							<FileText class="h-5 w-5 text-blue-600" />
							<span class="text-sm font-medium text-blue-800">Detected: CV/Resume</span>
						{/if}
					</div>

					{#if docType === 'cv' && missingFields.includes('email')}
						<div class="space-y-2">
							<Label for="email">Email address *</Label>
							<p class="text-xs text-gray-500">We'll send job matches here</p>
							<Input
								id="email"
								type="email"
								placeholder="you@example.com"
								bind:value={email}
								class={formError && !EMAIL_REGEX.test(email) ? 'border-red-500' : ''}
							/>
						</div>
					{/if}

					{#if docType === 'jd'}
						{#if missingFields.includes('company_contact')}
							<div class="space-y-4">
								<p class="text-sm text-gray-600">We need a way to contact your company:</p>

								<div class="space-y-2">
									<Label for="companyEmail">Company Email</Label>
									<Input
										id="companyEmail"
										type="email"
										placeholder="jobs@company.com"
										bind:value={companyEmail}
									/>
								</div>

								<div class="text-center text-xs text-gray-400">or</div>

								<div class="space-y-2">
									<Label for="companyWebsite">Company Website</Label>
									<Input
										id="companyWebsite"
										type="url"
										placeholder="https://company.com"
										bind:value={companyWebsite}
									/>
								</div>
							</div>
						{/if}

						{#if missingFields.includes('job_title')}
							<div class="space-y-2">
								<Label for="dialogJobTitle">Job Title *</Label>
								<Input
									id="dialogJobTitle"
									placeholder="e.g. Senior Backend Engineer"
									bind:value={jobTitle}
								/>
							</div>
						{/if}

						{#if !parsedData?.company_name && !companyName}
							<div class="space-y-2">
								<Label for="dialogCompanyName">Company Name</Label>
								<Input
									id="dialogCompanyName"
									placeholder="Your company name"
									bind:value={companyName}
								/>
							</div>
						{/if}

						{#if !parsedData?.description}
							<div class="space-y-2">
								<Label for="dialogDescription">Job Description</Label>
								<Textarea
									id="dialogDescription"
									placeholder="Brief description of the role..."
									bind:value={jobDescription}
									rows={4}
								/>
							</div>
						{/if}

						{#if !parsedData?.location}
							<div class="space-y-2">
								<Label for="dialogLocation">Location</Label>
								<Input
									id="dialogLocation"
									placeholder="e.g. Remote, San Francisco"
									bind:value={jobLocation}
								/>
							</div>
						{/if}
					{/if}

					{#if formError}
						<p class="text-sm text-red-500">{formError}</p>
					{/if}

					<Button class="w-full" onclick={handleMissingFieldsSubmit}>
						{docType === 'jd' ? 'Post Job' : 'Get Job Matches'}
					</Button>
				</div>
			{:else if step === 'success'}
				<div class="flex flex-col items-center py-6 text-center">
					<div class="mb-4 rounded-full {wasUpdated ? 'bg-blue-100' : 'bg-green-100'} p-3">
						{#if wasUpdated}
							<RefreshCw class="h-8 w-8 text-blue-600" />
						{:else}
							<CheckCircle class="h-8 w-8 text-green-600" />
						{/if}
					</div>
					<p class="text-gray-600">{message}</p>

					<div class="mt-6 w-full space-y-3 border-t pt-6">
						{#if isAppUser}
							<p class="text-sm font-medium text-gray-900">
								Your data will be linked to your account automatically on your next sign-in.
							</p>
							<Button class="w-full gap-2" onclick={handleGoToApp}>
								<ExternalLink class="h-4 w-4" />
								{docType === 'jd' ? 'Go to Dashboard' : 'Go to My Profile'}
							</Button>
						{:else}
							<p class="text-sm font-medium text-gray-900">
								{docType === 'jd'
									? 'Create an account to manage your job listings'
									: 'Create an account to track matches and manage your profile'}
							</p>
							<Button class="w-full gap-2" onclick={handleSignUp}>
								<UserPlus class="h-4 w-4" />
								Create Free Account
							</Button>
						{/if}
						<button
							type="button"
							class="w-full text-sm text-gray-500 hover:text-gray-700"
							onclick={() => (open = false)}
						>
							Maybe later
						</button>
					</div>
				</div>
			{:else if step === 'error'}
				<div class="flex flex-col items-center py-6 text-center">
					<div class="mb-4 rounded-full bg-red-100 p-3">
						{#if message.includes("couldn't identify")}
							<AlertCircle class="h-8 w-8 text-red-600" />
						{:else}
							<X class="h-8 w-8 text-red-600" />
						{/if}
					</div>
					<p class="mb-4 text-gray-600">{message}</p>
					<Button variant="outline" onclick={reset}>Try Again</Button>
				</div>
			{/if}
		</div>
	</Dialog.Content>
</Dialog.Root>
